import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import PhoneInput, {
  Country,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";

import styles from "./PhoneNumberInput.module.css";
import { CountryCode } from "libphonenumber-js";
import ThemeContext from "~/contexts/ThemeContext";

const DEFAULT_COUNTRY: Country = "US";

type PhoneNumberInputProps = {
  required?: boolean;
  id?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  allowedCountryCodes?: CountryCode[];
  defaultCountry?: CountryCode;
  disabled?: boolean;
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  required,
  id,
  name,
  placeholder,
  value,
  onChange,
  allowedCountryCodes,
  defaultCountry,
  disabled,
}) => {
  // Helper function to get the appropriate country to use
  const getInitialCountry = useCallback((): Country => {
    if (defaultCountry && allowedCountryCodes) {
      if (allowedCountryCodes.includes(defaultCountry)) {
        return defaultCountry as Country;
      }
      if (allowedCountryCodes.length > 0) {
        return allowedCountryCodes[0] as Country;
      }
    }
    return (defaultCountry as Country) || DEFAULT_COUNTRY;
  }, [defaultCountry, allowedCountryCodes]);

  const [selectedCountry, setSelectedCountry] = useState<Country>(
    getInitialCountry()
  );

  const { template } = useContext(ThemeContext);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const newCountry = getInitialCountry();
    setSelectedCountry(newCountry);
  }, [getInitialCountry, value]);

  useEffect(() => {
    const numberInput = wrapperRef.current?.querySelector('input[type="tel"]');
    if (numberInput instanceof HTMLInputElement) {
      numberInput.setCustomValidity(
        numberInput.value && !isValidPhoneNumber(numberInput.value)
          ? "Invalid phone number"
          : required && !numberInput.value
          ? "Phone number is required"
          : ""
      );
    }
  }, [required]);

  const handleCountryChange = (country?: CountryCode) => {
    if (country) {
      setSelectedCountry(country as Country);
    }
  };

  // Styles
  const inputStyles: Record<string, string> = {
    opacity: disabled ? "0.5" : "1",
    "--input-background-colour": template.global.input.backgroundColour,
    "--input-border-colour": template.global.input.borderColour,
    "--input-font-colour": template.global.input.fontColour,
    "--focus-input-background-colour":
      template.global.input.focusBackgroundColour,
    "--focus-input-border-colour": template.global.input.focusBorderColour,
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef} style={inputStyles}>
      <PhoneInput
        international
        withCountryCallingCode
        onCountryChange={handleCountryChange}
        value={value}
        onChange={(phoneNumber) => {
          onChange && onChange((phoneNumber ?? "").toString());

          const numberInput =
            wrapperRef.current?.querySelector('input[type="tel"]');
          if (numberInput instanceof HTMLInputElement) {
            numberInput.setCustomValidity(
              phoneNumber && !isValidPhoneNumber(phoneNumber)
                ? "Invalid phone number"
                : required && !phoneNumber
                ? "Phone number is required"
                : ""
            );
          }
        }}
        numberInputProps={{
          placeholder,
          id,
          name,
          required: value === undefined && required,
        }}
        defaultCountry={selectedCountry}
        countryCallingCodeEditable={false}
        disabled={disabled}
        countries={allowedCountryCodes}
      />
    </div>
  );
};

export default PhoneNumberInput;
